import React, { useState } from "react";

export interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  gradient?: string;   // legacy gradient (white icon on color bg)
  iconBg?: string;     // e.g. "bg-indigo-50"  — KPICard style
  iconColor?: string;  // e.g. "text-indigo-500" — KPICard style
  info?: string;       // tooltip text for the ⓘ button
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  gradient,
  iconBg,
  iconColor,
  info,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const usingNewStyle = !!(iconBg || iconColor);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center space-x-3 relative overflow-visible">
      {usingNewStyle ? (
        // KPICard-style: soft bg + colored icon
        <div className={`w-10 h-10 rounded-[15%] ${iconBg ?? 'bg-indigo-50'} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor ?? 'text-indigo-500'}`} />
        </div>
      ) : (
        // Legacy: gradient bg + white icon
        <div className={`w-10 h-10 bg-gradient-to-r ${gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}

      {/* Label + Value */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-[#46455499] uppercase">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>

      {/* Info icon with hover tooltip — top-right */}
      {info && (
        <div 
          className="absolute top-2 right-2"
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
        >
          <div
            className="w-4 h-4 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center transition-colors"
            aria-label="More information"
          >
            <span className="text-[9px] font-bold leading-none select-none">i</span>
          </div>

          {/* Tooltip */}
          {tooltipOpen && (
            <div
              className="absolute right-0 top-6 z-50 w-48 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl leading-relaxed"
            >
              <div className="absolute -top-1.5 right-1.5 w-3 h-3 bg-gray-900 rotate-45 rounded-sm" />
              <span className="relative z-10">{info}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatCard;
